import { Static } from '@sinclair/typebox';
import { DateString } from 'zapatos/db';
import { ParticipantPersonRelationship } from './atomic/participant/types';
import { AssetClass, CountryCode } from './atomic/types';
import {
    DownturnReaction,
    InvestmentPriority,
    UserEmployment,
    UserIdentity,
} from './atomic/user/types';
import {
    VerificationChecks,
    VerificationItem,
    VerificationRequired,
} from './atomic/verification/types';
import { EMPLOYMENT_INDUSTRY_MAP, EMPLOYMENT_ROLE_MAP } from './employment';
import { FinancialEntityTypename } from './financialEntities';
import { GoalType } from './graphql/generated';
import { PartnerContributionOption } from './react/goals';
import { app } from './zapatos';

export type AssetClassExposures = {
    name: Static<typeof AssetClass>;
    fraction: number;
}[];

export type FinancialEntityPosition = {
    name: string | null;
    symbol: string;
    industry: string | null;
    shares: number | null;
    sharePrice: number | null;
    totalValue: number | null;
    initialCost: number | null;
    totalReturnRealized: number | null;
    totalReturnUnrealized: number | null;
    taxLossesHarvested: number | null;
};

export type FinancialEntityPositions = FinancialEntityPosition[];

export type RecurrenceFreq = 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export interface RecurrenceBase {
    freq: RecurrenceFreq;
    interval: number;
}

export interface RecurrenceWeekly extends RecurrenceBase {
    freq: 'WEEKLY';
    interval: 1;
}

export interface RecurrenceBiweekly extends RecurrenceBase {
    freq: 'WEEKLY';
    interval: 2;
}

export interface RecurrenceMonthly extends RecurrenceBase {
    freq: 'MONTHLY';
    interval: 1;
}

export interface RecurrenceQuarterly extends RecurrenceBase {
    freq: 'MONTHLY';
    interval: 3;
}

export interface RecurrenceYearly extends RecurrenceBase {
    freq: 'YEARLY';
    interval: 1;
}

export type Recurrence =
    | RecurrenceWeekly
    | RecurrenceBiweekly
    | RecurrenceMonthly
    | RecurrenceQuarterly
    | RecurrenceYearly;

export type Schedule = {
    startDate: DateString;
    recurrence: Recurrence;
};

export type DateRange = {
    startDate: string;
    endDate: string;
};

export type SnapshotSource = {
    type: 'ATOMIC' | 'PLAID' | 'MONEY_KIT';
    id: string;
};

export interface CashManagementGoalInfo {
    inNumYears?: number;
    inNumYearsStr?: string;
    projectedAmount?: number;
}

export interface GeneralInvestingGoalInfo {
    earliestWithdrawal?: EarliestWithdrawal;
    inNumYears?: number;
    inNumYearsStr?: string;
    projectedAmount?: number;
}

export type EarliestWithdrawal =
    | '0_TO_2_YEARS'
    | '2_TO_5_YEARS'
    | '5_PLUS_YEARS';

export interface HomeDownPaymentGoalInfo {
    isCostKnown?: boolean;
    metro?: string;
    homeSize?: HomeSize;
    yearMonth?: string;
    projectedHomePriceInfo?: HomePriceInfo;
    homePrice?: number;
    downPaymentFraction?: number;
    includeClosingCostsAndFees?: boolean;
}

export type HomeSize =
    | 'CONDO'
    | '1_BED'
    | '2_BED'
    | '3_BED'
    | '4_BED'
    | '5_BED';

export interface HomePriceInfo {
    locationName: string;
    amount: number;
}

export interface EmergencyFundGoalInfo {
    jobSearchDuration?: JobSearchDuration;
    isHomeOwner?: boolean;
    monthlyExpenses?: number;
    projectedMonths?: number; // integer
    projectedAmount?: number;
    targetMonths?: number; // integer
    yearMonth?: string;
}

export type JobSearchDuration = 'SUB_2_MONTHS' | '2_PLUS_MONTHS';

// TODO: Extend this when we implement this goal
export type PayOffDebtGoalInfo = Record<string, unknown>;

export interface VacationGoalInfo {
    days?: number;
    people?: VacationPeople;
    continent?: VacationContinent;
    country?: VacationCountry;
    expenseLevel?: VacationExpenseLevel;
    yearMonth?: string;
    projectedAmount?: number;
}

export type VacationPeople =
    | 'ME'
    | 'ME_PLUS_1'
    | 'ME_PLUS_2'
    | 'ME_PLUS_3'
    | 'ME_PLUS_4'
    | 'ME_PLUS_5'
    | 'ME_PLUS_6'
    | 'ME_PLUS_7'
    | 'ME_PLUS_8';

export type VacationContinent =
    | 'Africa'
    | 'Asia'
    | 'Caribbean'
    | 'Central America'
    | 'Europe'
    | 'Middle East'
    | 'North America'
    | 'Pacific'
    | 'South America';

export type VacationCountry =
    | 'Afghanistan'
    | 'Albania'
    | 'Algeria'
    | 'Andorra'
    | 'Angola'
    | 'Antigua and Barbuda'
    | 'Argentina'
    | 'Armenia'
    | 'Aruba'
    | 'Australia'
    | 'Austria'
    | 'Azerbaijan'
    | 'Bahamas'
    | 'Bahrain'
    | 'Bangladesh'
    | 'Barbados'
    | 'Belarus'
    | 'Belgium'
    | 'Belize'
    | 'Benin'
    | 'Bermuda'
    | 'Bhutan'
    | 'Bolivia'
    | 'Bosnia and Herzegovina'
    | 'Botswana'
    | 'Brazil'
    | 'British Virgin Islands'
    | 'Brunei'
    | 'Bulgaria'
    | 'Burkina Faso'
    | 'Burundi'
    | 'Cambodia'
    | 'Cameroon'
    | 'Canada'
    | 'Cabo Verde'
    | 'Cayman Islands'
    | 'Central African Republic'
    | 'Chad'
    | 'Chile'
    | 'China'
    | 'Colombia'
    | 'Comoros'
    | 'Congo'
    | 'Costa Rica'
    | "Côte d'Ivoire"
    | 'Croatia'
    | 'Cuba'
    | 'Curacao'
    | 'Cyprus'
    | 'Czech Republic'
    | 'Denmark'
    | 'Djibouti'
    | 'Dominica'
    | 'Dominican Republic'
    | 'Ecuador'
    | 'Egypt'
    | 'El Salvador'
    | 'Equatorial Guinea'
    | 'Eritrea'
    | 'Estonia'
    | 'Ethiopia'
    | 'Fiji'
    | 'Finland'
    | 'France'
    | 'Gabon'
    | 'Gambia'
    | 'Georgia'
    | 'Germany'
    | 'Ghana'
    | 'Greece'
    | 'Grenada'
    | 'Guadeloupe'
    | 'Guatemala'
    | 'Guinea'
    | 'Guinea-Bissau'
    | 'Guyana'
    | 'Haiti'
    | 'Honduras'
    | 'Hong Kong'
    | 'Hungary'
    | 'Iceland'
    | 'India'
    | 'Indonesia'
    | 'Iran'
    | 'Iraq'
    | 'Ireland'
    | 'Israel'
    | 'Italy'
    | 'Jamaica'
    | 'Japan'
    | 'Jordan'
    | 'Kazakhstan'
    | 'Kenya'
    | 'Kiribati'
    | "Korea (Democratic People's Republic of)"
    | 'South Korea'
    | 'Kuwait'
    | 'Kyrgyzstan'
    | "Lao People's Democratic Republic"
    | 'Latvia'
    | 'Lebanon'
    | 'Lesotho'
    | 'Liberia'
    | 'Libya'
    | 'Liechtenstein'
    | 'Lithuania'
    | 'Luxembourg'
    | 'North Macedonia'
    | 'Macau'
    | 'Madagascar'
    | 'Malawi'
    | 'Malaysia'
    | 'Maldives'
    | 'Mali'
    | 'Malta'
    | 'Martinique'
    | 'Marshall Islands'
    | 'Mauritania'
    | 'Mauritius'
    | 'Mexico'
    | 'Micronesia'
    | 'Morocco'
    | 'Moldova'
    | 'Monaco'
    | 'Mongolia'
    | 'Montenegro'
    | 'Mozambique'
    | 'Myanmar'
    | 'Namibia'
    | 'Nauru'
    | 'Nepal'
    | 'Netherlands'
    | 'New Zealand'
    | 'Nicaragua'
    | 'Niger'
    | 'Nigeria'
    | 'Norway'
    | 'Oman'
    | 'Pakistan'
    | 'Palau'
    | 'Panama'
    | 'Papua New Guinea'
    | 'Paraguay'
    | 'Peru'
    | 'Philippines'
    | 'Poland'
    | 'Portugal'
    | 'Puerto Rico'
    | 'Qatar'
    | 'Romania'
    | 'Russia'
    | 'Rwanda'
    | 'Saint Kitts and Nevis'
    | 'Saint Lucia'
    | 'Saint Vincent and the Grenadines'
    | 'Samoa'
    | 'Sao Tome and Principe'
    | 'Saudi Arabia'
    | 'Senegal'
    | 'Serbia'
    | 'Seychelles'
    | 'Sierra Leone'
    | 'Singapore'
    | 'Slovakia'
    | 'Slovenia'
    | 'Solomon Islands'
    | 'Somalia'
    | 'South Africa'
    | 'South Sudan'
    | 'Spain'
    | 'Sri Lanka'
    | 'Sudan'
    | 'Suriname'
    | 'Eswatini'
    | 'Sweden'
    | 'Switzerland'
    | 'Syria'
    | 'Taiwan'
    | 'Tahiti'
    | 'Tajikistan'
    | 'Tanzania'
    | 'Thailand'
    | 'Togo'
    | 'Tonga'
    | 'Trinidad and Tobago'
    | 'Tunisia'
    | 'Turks and Caicos'
    | 'Turkey'
    | 'Turkmenistan'
    | 'Tuvalu'
    | 'Uganda'
    | 'Ukraine'
    | 'United Arab Emirates'
    | 'United Kingdom'
    | 'United States of America'
    | 'US Virgin Islands'
    | 'Uruguay'
    | 'Uzbekistan'
    | 'Vanuatu'
    | 'Venezuela'
    | 'Vietnam'
    | 'Yemen'
    | 'Zambia'
    | 'Zimbabwe';

export type VacationExpenseLevel = 'BUDGET' | 'MID_RANGE' | 'LUXURY';

export interface WeddingGoalInfo {
    guests?: number;
    expenseLevel?: WeddingExpenseLevel;
    yearMonth?: string;
    projectedAmount?: number;
}

export type WeddingExpenseLevel = 'BUDGET' | 'MODERATE' | 'GENEROUS' | 'LUXURY';

export interface CarGoalInfo {
    isCarPriceKnown?: boolean;
    purchaseMethod?: CarPurchaseMethod;
    newOrUsed?: NewOrUsed;
    category?: CarCategory;
    yearMonth?: string;
    projectedCarPrice?: number;
    carPrice?: number;
    downPaymentFraction?: number;
}

export type CarPurchaseMethod = 'IN_FULL' | 'LOAN';

export type NewOrUsed = 'NEW' | 'USED';

export type CarCategory =
    | 'COMPACT'
    | 'CROSSOVER'
    | 'SUV'
    | 'PICKUP'
    | 'MINIVAN'
    | 'LUXURY_SEDAN'
    | 'LUXURY_SUV';

export interface BabyGoalInfo {
    isInsured?: boolean;
    isSavingForLostIncome?: boolean;
    weeksOfIncome?: number;
    yearMonth?: string;
    projectedMedicalCostInfo?: BabyMedicalCostInfo;
    projectedLostIncomeAmount?: number;
}

export interface BabyMedicalCostInfo {
    locationName: string;
    amount: number;
}

export interface DaycareGoalInfo {
    quality?: DaycareQuality;
    ageLevel?: DaycareAgeLevel;
    yearMonth?: string;
    projectedAmountInfo?: DaycareCostInfo;
}

export type DaycareQuality = 'STANDARD' | 'HIGHER';

export type DaycareAgeLevel = 'INFANT' | 'TODDLER' | 'PRESCHOOLER';

export interface DaycareCostInfo {
    locationName: string;
    amount: number;
}

export type CollegeGoalInfo = Record<string, unknown>;
export type RetirementGoalInfo = Record<string, unknown>;

export interface CustomGoalInfo {
    yearMonth?: string;
}

export type GoalTypeInfo =
    | CashManagementGoalInfo
    | GeneralInvestingGoalInfo
    | HomeDownPaymentGoalInfo
    | EmergencyFundGoalInfo
    | PayOffDebtGoalInfo
    | VacationGoalInfo
    | CollegeGoalInfo
    | CarGoalInfo
    | WeddingGoalInfo
    | BabyGoalInfo
    | DaycareGoalInfo
    | RetirementGoalInfo
    | CustomGoalInfo;

export type GoalAccountInvestmentType =
    | 'HIGH_YIELD_CASH'
    | 'DIRECT_INDEXING'
    | 'CLASSIC_PORTFOLIO';

export type GoalAccountOnboardingInfo =
    | RetirementAccountOnboardingInfo
    | DefaultGoalAccountOnboardingInfo;

export interface DefaultGoalAccountOnboardingInfo {
    infoType: 'DEFAULT';
    recommendedRiskScore?: number | null;
    riskScore?: number;
    initialDepositPlan?: number;
    transactionSchedulePlan?: TransactionSchedulePlan;
    fundingAccountId?: string;
    lastCompletedStep?: GoalAccountOnboardingStep;
    partnerInitialDepositPlan?: number;
    partnerTransactionSchedulePlan?: TransactionSchedulePlan;
    investmentType?: GoalAccountInvestmentType;
    partnerContribution?: PartnerContributionOption | null;
}

export interface RetirementAccountOnboardingInfo {
    infoType: 'RETIREMENT';
    iraType: IraType;
    lastCompletedRetirementStep?: RetirementAccountOnboardingStep;
    primaryBeneficiaries?: OnboardingInfoBeneficiary[];
    secondaryBeneficiaries?: OnboardingInfoBeneficiary[];
    riskScore?: number;
    initialDepositPlan?: number;
    transactionSchedulePlan?: TransactionSchedulePlan;
    fundingAccountId?: string;
    investmentType?: 'DIRECT_INDEXING'; // TODO: Support other types
    partnerContribution?: PartnerContributionOption | null;
}

export type IraType = 'TRADITIONAL' | 'ROTH';

export type RetirementAccountOnboardingStep =
    | 'INTRO'
    | 'BENEFICIARY'
    | 'PORTFOLIO'
    | 'PLAN'
    | 'FUNDING'
    | 'REVIEW'
    | 'TERMS'
    | 'CELEBRATION';

export interface OnboardingInfoBeneficiary {
    id: string;
    firstName: string;
    lastName: string;
    relationship: BeneficiaryRelationship;
    dob: string;
    fraction: number;
}

export type BeneficiaryRelationship = Static<
    typeof ParticipantPersonRelationship
>;

export type GoalAccountOnboardingStep =
    | 'ACCOUNT_TYPE'
    | 'TARGET'
    | 'PORTFOLIO'
    | 'PLAN'
    | 'CONNECT'
    | 'CONFIRM'
    | 'COMPLETE';

export type TradeInfo = { symbol: string } | { cusip: string };

export type Horizon = 'SHORT_TERM' | 'MEDIUM_TERM' | 'LONG_TERM';
export type RecommendedRiskScoreByHorizon = Record<Horizon, number>;

export interface AmountRecurrence {
    amount: number;
    recurrence: Recurrence;
}

// TODO: Consider removing this and just using the GraphQL codegen'd type
export interface TransactionSchedulePlan {
    amount: number;
    recurrence: Recurrence;
    startDate: string;
    nextTransactionDate?: string | null;
}

interface RecurrenceConfig {
    freqString: string;
    adverbPhrase: string;
    unit: 'day' | 'week' | 'month' | 'year';
    perYear: number;
}

export const getRecurrenceConfig = (r: RecurrenceBase): RecurrenceConfig => {
    switch (r.freq) {
        case 'WEEKLY': {
            const perYear = 52 / r.interval;
            if (r.interval === 2) {
                return {
                    freqString: '2 weeks',
                    adverbPhrase: 'biweekly',
                    unit: 'week',
                    perYear,
                };
            }
            return {
                freqString: r.interval === 1 ? 'week' : r.interval + ' weeks',
                adverbPhrase:
                    r.interval === 1 ? 'weekly' : `every ${r.interval} weeks`,
                unit: 'week',
                perYear,
            };
        }
        case 'MONTHLY': {
            const perYear = 12 / r.interval;
            if (r.interval === 3) {
                return {
                    freqString: 'quarter',
                    adverbPhrase: 'quarterly',
                    unit: 'month',
                    perYear,
                };
            }
            return {
                freqString: r.interval === 1 ? 'month' : r.interval + ' months',
                adverbPhrase:
                    r.interval === 1 ? 'monthly' : `every ${r.interval} months`,
                unit: 'month',
                perYear,
            };
        }
        case 'YEARLY': {
            return {
                freqString: r.interval === 1 ? 'year' : r.interval + ' years',
                adverbPhrase:
                    r.interval === 1 ? 'yearly' : `every ${r.interval} years`,
                unit: 'year',
                perYear: 1,
            };
        }
    }
};

// Ref: https://github.com/sindresorhus/type-fest/blob/e02f228f6391bb2b26c32a55dfe1e3aa2386d515/source/conditional-keys.d.ts#L33C1-L47C3
export type ConditionalKeys<Base, Condition> = NonNullable<
    {
        [Key in keyof Base]: Base[Key] extends Condition ? Key : never;
    }[keyof Base]
>;

export type Unpacked<T> =
    T extends Array<infer U> ? U : T extends ReadonlyArray<infer U> ? U : T;

// Ref: https://typeofnan.dev/making-every-object-property-nullable-in-typescript/
export type Nullable<T> = { [K in keyof T]: T[K] | null };

export type ActivityTransactionStatus = 'PENDING' | 'COMPLETED' | 'FAILED';

export type ActivityTypeInfo =
    | {
          type: 'DIVIDEND_REALIZED';
          amount: number;
          feId: string;
          goalId: string;
          transactionEntryId: string;
      }
    | { type: 'FE_CREATED'; feId: string }
    | {
          type: 'FE_DEPOSIT_CREATED';
          feId: string;
          transactionId: string;
          amount: number;
          sourceId: string;
          goalId?: string;
          transactionScheduleId?: string;
          transactionStatus?: ActivityTransactionStatus;
          plannedProcessingDate?: string;
      }
    | {
          type: 'FE_WITHDRAWAL_CREATED';
          feId: string;
          transactionId: string;
          amount: number;
          destinationId: string;
          goalId?: string;
          transactionScheduleId?: string;
          transactionStatus?: ActivityTransactionStatus;
          plannedProcessingDate?: string;
      }
    | {
          type: 'FEE_REALIZED';
          amount: number;
          feId: string;
          goalId: string;
          transactionEntryId: string;
      }
    | {
          type: 'GOAL_CONTRIBUTING_PAUSED';
          feId?: string; // undefined in some prod DB records
          goalId: string;
      }
    | {
          type: 'GOAL_CONTRIBUTING_RESUMED';
          feId?: string; // undefined in some prod DB records
          goalId: string;
      }
    | {
          type: 'GOAL_CONTRIBUTION_SCHEDULE_SET';
          feId?: string; // undefined in some prod DB records
          goalId: string;
          amount: number;
          recurrence: Recurrence;
      }
    | {
          type: 'GOAL_CONTRIBUTION_SCHEDULE_CHANGED';
          feId?: string; // undefined in some prod DB records
          goalId: string;
          amount: number;
          recurrence: Recurrence;
      }
    | { type: 'GOAL_CREATED'; goalId: string }
    | { type: 'GOAL_FUNDING_STARTED'; goalId: string; feId: string }
    | {
          type: 'INTEREST_REALIZED';
          amount: number;
          feId: string;
          goalId: string;
          transactionEntryId: string;
      }
    | {
          type: 'PLENTY_IRA_CREATED';
          goalId: string;
          feId: string;
          iraType: IraType;
      }
    | {
          type: 'TAX_REALIZED';
          amount: number;
          feId: string;
          goalId: string;
          transactionEntryId: string;
      }
    | {
          type: 'STOPPED_TRANSACTION_SCHEDULE';
          feId: string;
          goalId: string;
      }
    | {
          type: 'PARTNER_LEFT_SPLIT_GOAL';
          goalId: string;
      };

export type ActivityType = ActivityTypeInfo['type'];

export type AtomicVerificationRequired = Static<typeof VerificationRequired>;
export type AtomicVerificationChecks = Static<typeof VerificationChecks>;
export type AtomicVerificationItem = Static<typeof VerificationItem>;
export type AtomicInvestmentPriority = Static<typeof InvestmentPriority>;
export type AtomicDownturnReaction = Static<typeof DownturnReaction>;

export type AtomicStatusTypeInfo = {
    required?: AtomicVerificationRequired;
    checks?: AtomicVerificationChecks;
};

export type ShareFinancesAnswer =
    | 'everything_separate'
    | 'some_separate_some_shared'
    | 'everything_shared'
    | 'skipped';
export type SplitExpensesAnswer =
    | 'everything_separate'
    | 'some_separate_some_shared'
    | 'everything_shared'
    | 'skipped';
export type DepositPaychecksAnswer =
    | 'personal_accounts'
    | 'joint_account'
    | 'other'
    | 'skipped';

export type OnboardingData = {
    hasPartner?: boolean;
    hasChildren?: boolean;
    partner?: {
        preferredName?: string;
        relationshipStatus?: app.relationship_status;
        email?: string;
    };
    children?: {
        name: string;
        dob: string;
    }[];
    country?: Country;
    residenceStatus?: ResidenceStatus;
    employmentStatus?: EmploymentStatus;
    employerName?: string;
    employerAddress?: {
        address1?: string;
        address2?: string;
        locality?: string;
        region?: string;
        postalCode?: string;
    };
    industry?: EmploymentIndustry;
    role?: EmploymentRole;
    employmentStartYearMonth?: string; // only for self employed, YYYYMM format
    hasConflictOfInterest?: boolean;
    requestedCountry?: Country;
    shareFinances?: ShareFinancesAnswer;
    splitExpenses?: SplitExpensesAnswer;
    depositPaychecks?: DepositPaychecksAnswer;
    isSingleplayer?: boolean;
};

export type Country = Static<typeof CountryCode>;

export type ResidenceStatus = Static<typeof UserIdentity>['residence_status'];

export type EmploymentStatus = Static<typeof UserEmployment.properties.status>;

export type EmploymentIndustry = keyof typeof EMPLOYMENT_INDUSTRY_MAP;

export type EmploymentRole = keyof typeof EMPLOYMENT_ROLE_MAP;

export type SupportedValue =
    | 'END_POVERTY_HUNGER'
    | 'ELIMINATE_INEQUALITY'
    | 'EMPOWER_WOMEN'
    | 'PROTECT_NATURE'
    | 'FIGHT_CLIMATE_CHANGE';

export type AvoidIndustry =
    | 'TOBACCO'
    | 'PRIVATE_PRISONS'
    | 'ANIMAL_TESTING'
    | 'WEAPONS'
    | 'GAMBLING'
    | 'PREDATORY_LENDING';

export type InvestmentProfile = {
    investment_priority?: AtomicInvestmentPriority;
    downturn_reaction?: AtomicDownturnReaction;
    supported_values?: SupportedValue[];
    avoid_industries?: AvoidIndustry[];
};

export type PaymentMethodInfo = {
    type: 'CARD';
    brand?: string;
    mask?: string;
    expirationMonth?: number;
    expirationYear?: number;
};

export interface EncryptedData {
    /** Base64-encoded initialization vector (IV) */
    iv: string;
    /** Base64-encoded ciphertext */
    ciphertext: string;
    /** Base64-encoded auth tag */
    tag: string;
}

// https://stackoverflow.com/a/69328045
// Make a subset of types required within a parent type
export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export interface Attachment {
    /** S3 key */
    key: string;
    /** file size in bytes */
    size: number;
    /** original client-side MIME type */
    type?: string;
    /** original client-side filename */
    name?: string;
}

export type Attachments = Attachment[];

export interface PresignedPost {
    url: string;
    fields: Record<string, string | undefined>;
}

export interface WarmWelcomes {
    dashboard?: boolean;
    goals?: boolean;
    pageToggle?: boolean;
}

type JointAccountConsentInfo = {
    consentType: 'JOINT_ACCOUNT_CONSENT';
    partnerUserId?: string;
    partnerFirstName?: string;
    partnerLastName?: string;
};

export type ConsentInfo = JointAccountConsentInfo;

export type UtmParams = {
    utmCampaign?: string;
    utmSource?: string;
    utmMedium?: string;
    utmTerm?: string;
    utmContent?: string;
};

export type CancellationReason =
    | 'Too expensive'
    | 'Technical issue'
    | 'Didn’t use Plenty enough'
    | 'Switched to another investment platform'
    | 'Investing didn’t meet my expectations'
    | 'Cash flow management didn’t meet my expectations'
    | 'Goal setting didn’t meet my expectations'
    | 'I can’t connect to all my accounts (please note below so we can prioritize them)'
    | 'Other';

export type MembershipPromotionCode = {
    id: string;
    code: string;
    amountOff: number | null;
    percentOff: number | null;
};

export type LinkTo = {
    id: string;
    type: FinancialEntityTypename;
};

export type GoalPreferenceStatus = 'DEFAULT' | 'HIDDEN' | 'FAVORITE';
export type GoalPreferences = Partial<Record<GoalType, GoalPreferenceStatus>>;

export interface SpendingComparisonInputs {
    income?: number;
    zipCode?: string;
}

export type SpendingCategoryName =
    | 'Food & Beverage'
    | 'Housing'
    | 'Shopping & Entertainment'
    | 'Transportation'
    | 'Healthcare';

export type PageMode = 'PUBLIC' | 'MINE';

export type LogErrorFcn = (
    error: unknown,
    context?: Record<string, unknown>
) => void;
